import React, { useRef, useCallback, useEffect, useMemo } from 'react';
import { Page } from '../types/storybook';
import { PreflightIssue } from '../utils/pagePreflight';
import './PagesList.css';

const EMPTY_THUMBNAIL_DATA_URL = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="1" height="1"/%3E';

interface PagesListProps {
  pages: Page[];
  currentPageIndex: number;
  onPageSelect: (index: number) => void;
  onPageReorder: (fromIndices: number[], toIndex: number) => void;
  onAddPage: () => void;
  onDeletePage: (index: number) => void;
  onOpenPlots?: () => void;
  preflightIssues?: PreflightIssue[];
  preflightCheckedPageIds?: ReadonlySet<string>;
  onOpenPageIssues?: (pageId: string) => void;
  pageAspectRatio?: number;
}

const PagesList: React.FC<PagesListProps> = ({
  pages,
  currentPageIndex,
  onPageSelect,
  onPageReorder,
  onAddPage,
  onDeletePage,
  onOpenPlots,
  preflightIssues = [],
  preflightCheckedPageIds,
  onOpenPageIssues,
  pageAspectRatio = 3 / 4
}) => {
  const [draggedIndices, setDraggedIndices] = React.useState<number[]>([]);
  const [dragOverIndex, setDragOverIndex] = React.useState<number | null>(null);
  const [selectedIndices, setSelectedIndices] = React.useState<Set<number>>(new Set());
  const [lastClickedIndex, setLastClickedIndex] = React.useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const autoScrollRef = useRef<number | null>(null);
  const issuesByPage = useMemo(() => {
    const grouped = new Map<string, { errors: number; warnings: number }>();
    for (const issue of preflightIssues) {
      const counts = grouped.get(issue.pageId) || { errors: 0, warnings: 0 };
      if (issue.severity === 'error') counts.errors += 1;
      else counts.warnings += 1;
      grouped.set(issue.pageId, counts);
    }
    return grouped;
  }, [preflightIssues]);

  // Auto-scroll while dragging near edges
  const startAutoScroll = useCallback((clientY: number) => {
    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const scrollZone = 50; // pixels from edge to trigger scroll
    const scrollSpeed = 8;

    if (autoScrollRef.current) {
      cancelAnimationFrame(autoScrollRef.current);
      autoScrollRef.current = null;
    }

    const scroll = () => {
      if (!containerRef.current) return;
      const distFromTop = clientY - rect.top;
      const distFromBottom = rect.bottom - clientY;

      if (distFromTop < scrollZone && container.scrollTop > 0) {
        container.scrollTop -= scrollSpeed;
        autoScrollRef.current = requestAnimationFrame(scroll);
      } else if (distFromBottom < scrollZone && container.scrollTop < container.scrollHeight - container.clientHeight) {
        container.scrollTop += scrollSpeed;
        autoScrollRef.current = requestAnimationFrame(scroll);
      }
    };

    const distFromTop = clientY - rect.top;
    const distFromBottom = rect.bottom - clientY;
    if (distFromTop < scrollZone || distFromBottom < scrollZone) {
      autoScrollRef.current = requestAnimationFrame(scroll);
    }
  }, []);

  const stopAutoScroll = useCallback(() => {
    if (autoScrollRef.current) {
      cancelAnimationFrame(autoScrollRef.current);
      autoScrollRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => stopAutoScroll();
  }, [stopAutoScroll]);

  const handleClick = (e: React.MouseEvent, index: number) => {
    const page = pages[index];

    if (e.shiftKey && lastClickedIndex !== null) {
      // Shift-click: range select
      e.preventDefault();
      const start = Math.min(lastClickedIndex, index);
      const end = Math.max(lastClickedIndex, index);
      const newSelection = new Set(selectedIndices);
      for (let i = start; i <= end; i++) {
        if (!pages[i].isSystemPage) {
          newSelection.add(i);
        }
      }
      setSelectedIndices(newSelection);
    } else if (e.ctrlKey || e.metaKey) {
      // Ctrl/Cmd-click: toggle individual
      e.preventDefault();
      if (page.isSystemPage) return;
      const newSelection = new Set(selectedIndices);
      if (newSelection.has(index)) {
        newSelection.delete(index);
      } else {
        newSelection.add(index);
      }
      setSelectedIndices(newSelection);
      setLastClickedIndex(index);
    } else {
      // Normal click: select page and clear multi-selection
      setSelectedIndices(new Set());
      setLastClickedIndex(index);
      onPageSelect(index);
    }
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    const page = pages[index];
    if (page.isSystemPage) {
      e.preventDefault();
      return;
    }

    // If dragging a selected page, drag all selected; otherwise drag just this one
    let indicesToDrag: number[];
    if (selectedIndices.has(index)) {
      indicesToDrag = Array.from(selectedIndices).sort((a, b) => a - b);
    } else {
      indicesToDrag = [index];
      setSelectedIndices(new Set([index]));
    }

    setDraggedIndices(indicesToDrag);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', indicesToDrag.join(','));

    // Custom drag image showing count
    if (indicesToDrag.length > 1) {
      const badge = document.createElement('div');
      badge.textContent = `${indicesToDrag.length} pages`;
      badge.style.cssText = 'background:#4F46E5;color:white;padding:6px 12px;border-radius:6px;font-size:13px;font-weight:600;position:absolute;top:-9999px';
      document.body.appendChild(badge);
      e.dataTransfer.setDragImage(badge, 40, 16);
      setTimeout(() => document.body.removeChild(badge), 0);
    }
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    const page = pages[index];
    if (page.isSystemPage) {
      e.dataTransfer.dropEffect = 'none';
      return;
    }
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
    startAutoScroll(e.clientY);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    stopAutoScroll();
    if (draggedIndices.length > 0 && !draggedIndices.includes(dropIndex)) {
      onPageReorder(draggedIndices, dropIndex);
    }
    setDraggedIndices([]);
    setDragOverIndex(null);
    setSelectedIndices(new Set());
  };

  const handleDragEnd = () => {
    stopAutoScroll();
    setDraggedIndices([]);
    setDragOverIndex(null);
  };

  // Handle drag over the container itself for auto-scroll
  const handleContainerDragOver = (e: React.DragEvent) => {
    startAutoScroll(e.clientY);
  };

  return (
    <div className="pages-list">
      <div className="pages-list-header">
        <h3>Pages</h3>
        <div className="pages-header-buttons">
          {selectedIndices.size > 0 && (
            <span className="selection-badge">
              {selectedIndices.size} selected
            </span>
          )}
          {onOpenPlots && (
            <button 
              className="btn btn-secondary btn-small"
              onClick={onOpenPlots}
              title="Plan your story"
            >
              Story plan
            </button>
          )}
          <button 
            className="btn btn-primary btn-small"
            onClick={onAddPage}
            title="Add new page"
          >
            +
          </button>
        </div>
      </div>

      <div
        className="pages-container"
        ref={containerRef}
        onDragOver={handleContainerDragOver}
      >
        {pages.map((page, index) => {
          const issueCounts = issuesByPage.get(page.id) || { errors: 0, warnings: 0 };
          const wasChecked = preflightCheckedPageIds?.has(page.id)
            || issueCounts.errors > 0
            || issueCounts.warnings > 0;
          const pageLabel = page.pageTitle || `Page ${page.pageNumber || index + 1}`;
          const diagnosticLabel = issueCounts.errors > 0 || issueCounts.warnings > 0
            ? `${pageLabel}: ${issueCounts.errors} blocking ${issueCounts.errors === 1 ? 'issue' : 'issues'} and ${issueCounts.warnings} ${issueCounts.warnings === 1 ? 'warning' : 'warnings'}`
            : `${pageLabel}: layout check passed`;

          return <div
            key={page.id}
            className={`page-item ${index === currentPageIndex ? 'active' : ''} ${
              dragOverIndex === index ? 'drag-over' : ''
            } ${draggedIndices.includes(index) ? 'dragging' : ''} ${
              page.isSystemPage ? 'system-page' : ''
            } ${selectedIndices.has(index) ? 'multi-selected' : ''}`}
            onClick={(e) => handleClick(e, index)}
            draggable={!page.isSystemPage}
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
          >
            <div className="page-thumbnail">
              <div 
                className="page-preview"
                data-testid={index === currentPageIndex ? 'canonical-page-thumbnail' : undefined}
                data-thumbnail-state={page.thumbnail ? 'ready' : 'placeholder'}
                style={{
                  backgroundColor: page.backgroundColor,
                  aspectRatio: String(pageAspectRatio),
                  height: 'auto'
                }}
              >
                <img
                  src={page.thumbnail || EMPTY_THUMBNAIL_DATA_URL}
                  alt={page.thumbnail ? (page.pageTitle || `Page ${page.pageNumber || index + 1}`) : ''}
                  aria-hidden={page.thumbnail ? undefined : true}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                  }}
                />
                {!page.thumbnail && (
                  <span className="page-number">{page.pageTitle ? 'Title' : index}</span>
                )}
              </div>
            </div>
            
            <div className="page-info">
              <div className="page-title">
                {page.pageTitle || `Page ${index}`}
              </div>
              <div className="page-stats">
                {page.textElements.length} text, {page.imageElements.length} images
              </div>
              {wasChecked && (
                issueCounts.errors > 0 || issueCounts.warnings > 0 ? (
                  <button
                    type="button"
                    className={`page-preflight-badge ${issueCounts.errors > 0 ? 'has-errors' : 'has-warnings'}`}
                    data-testid="page-preflight-badge"
                    data-page-id={page.id}
                    data-severity={issueCounts.errors > 0 ? 'error' : 'warning'}
                    aria-label={diagnosticLabel}
                    title={diagnosticLabel}
                    onClick={(event) => {
                      event.stopPropagation();
                      onOpenPageIssues?.(page.id);
                    }}
                  >
                    {issueCounts.errors > 0 && (
                      <span data-testid={`page-preflight-errors-${page.id}`}>
                        <span aria-hidden="true">●</span> {issueCounts.errors}
                      </span>
                    )}
                    {issueCounts.warnings > 0 && (
                      <span data-testid={`page-preflight-warnings-${page.id}`}>
                        <span aria-hidden="true">▲</span> {issueCounts.warnings}
                      </span>
                    )}
                  </button>
                ) : (
                  <span
                    className="page-preflight-badge is-clean"
                    data-testid="page-preflight-badge"
                    data-page-id={page.id}
                    data-severity="clean"
                    role="status"
                    aria-label={diagnosticLabel}
                    title={diagnosticLabel}
                  >
                    <span aria-hidden="true">✓</span> Ready
                  </span>
                )
              )}
            </div>

            {pages.length > 3 && !page.isSystemPage && (
              <button
                className="delete-page-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeletePage(index);
                }}
                title="Delete page"
              >
                ×
              </button>
            )}
          </div>;
        })}
      </div>
    </div>
  );
};

export default PagesList;
